import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import {
  Step4_CalendarRoute,
  Step5_CalendarSuccessRoute,
  Step6_ScheduleInterviewRoute,
  VacancyEditorRoute,
  RecruiterWorkspaceRoute,
  VacantePublicViewRoute,
  VacancyDashboardRoute,
  RecruiterHomeRoute,
  GoogleAuthCallbackRoute,
  CheckoutRoute,
  VacancyQuestionsReviewRoute,
  RecruiterSettingsRoute,
  VacancyMetricsRoute,
  VacancyPublishedFichaPreviewRoute,
  PendingApprovalRoute,
} from './RouteWrappers'
import { ProtectedRoute, ProtectedOutlet } from './ProtectedRoute'
import { RecruiterLayout } from '../views-react/recruiter/RecruiterLayout'

// Lazy load initial routes
const HomeRoute = React.lazy(() => import('./HomeRoute').then((m) => ({ default: m.HomeRoute })))
const CandidateLayout = React.lazy(() =>
  import('../views-react/candidate/CandidateLayout').then((m) => ({ default: m.CandidateLayout })),
)
const CandidateDashboard = React.lazy(() =>
  import('../views-react/candidate/CandidateDashboard').then((m) => ({ default: m.CandidateDashboard })),
)
const CandidateApplicationsPage = React.lazy(() =>
  import('../views-react/candidate/CandidateApplicationsPage').then((m) => ({
    default: m.CandidateApplicationsPage,
  })),
)
const CandidateApplicationDetailPage = React.lazy(() =>
  import('../views-react/candidate/CandidateApplicationDetailPage').then((m) => ({
    default: m.CandidateApplicationDetailPage,
  })),
)
const CandidateJobsPage = React.lazy(() =>
  import('../views-react/candidate/CandidateJobsPage').then((m) => ({ default: m.CandidateJobsPage })),
)
const CandidateDocuments = React.lazy(() =>
  import('../views-react/candidate/CandidateDocuments').then((m) => ({ default: m.CandidateDocuments })),
)
const CandidateSettingsPage = React.lazy(() =>
  import('../views-react/candidate/CandidateSettingsPage').then((m) => ({ default: m.CandidateSettingsPage })),
)
const CandidateApplyPage = React.lazy(() =>
  import('../views-react/candidate/CandidateApplyPage').then((m) => ({ default: m.CandidateApplyPage })),
)
const AdminDashboard = React.lazy(() =>
  import('../views-react/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
)
const AdminUsers = React.lazy(() =>
  import('../views-react/admin/AdminUsers').then((m) => ({ default: m.AdminUsers })),
)
const AdminAudit = React.lazy(() =>
  import('../views-react/admin/AdminAudit').then((m) => ({ default: m.AdminAudit })),
)
const AdminAIMonitoring = React.lazy(() =>
  import('../views-react/admin/AdminAIMonitoring').then((m) => ({ default: m.AdminAIMonitoring })),
)

// Páginas legales públicas. Sin ProtectedRoute y sin leer el token: la
// verificación de Google exige poder abrirlas sin iniciar sesión, y hasta ahora
// el aviso de privacidad sólo existía como modal dentro del flujo de
// postulación — para leerlo había que entrar y empezar a postularse.
const PortadaPublica = React.lazy(() =>
  import('../views-react/shared/legal/PortadaPublica').then((m) => ({ default: m.PortadaPublica })),
)
const AvisoPrivacidadPage = React.lazy(() =>
  import('../views-react/shared/legal/PaginaLegal').then((m) => ({
    default: m.AvisoPrivacidadPage,
  })),
)
const TerminosPage = React.lazy(() =>
  import('../views-react/shared/legal/PaginaLegal').then((m) => ({ default: m.TerminosPage })),
)

const rutaPublica = (elemento: React.ComponentType) =>
  React.createElement(
    Suspense,
    { fallback: React.createElement(LoadingFallback) },
    React.createElement(elemento),
  )

const LoadingFallback = () =>
  React.createElement(
    'div',
    {
      className: 'auth-loading-content',
      style: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    React.createElement(
      'div',
      { className: 'spinner-container' },
      React.createElement('div', { className: 'spinner' }),
    ),
  )

export const router = createBrowserRouter([
  {
    path: '/',
    element: React.createElement(
      Suspense,
      { fallback: React.createElement(LoadingFallback) },
      React.createElement(HomeRoute),
    ),
  },
  {
    path: '/home',
    element: React.createElement(ProtectedRoute, {
      requiresAuth: true,
      children: React.createElement(
        Suspense,
        { fallback: React.createElement(LoadingFallback) },
        React.createElement(CandidateLayout),
      ),
    }),
    children: [
      {
        index: true,
        element: React.createElement(Navigate, { to: '/home/dashboard', replace: true }),
      },
      {
        path: 'dashboard',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateDashboard),
        ),
      },
      {
        path: 'applications/:jobId',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateApplicationDetailPage),
        ),
      },
      {
        path: 'applications',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateApplicationsPage),
        ),
      },
      {
        path: 'jobs',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateJobsPage),
        ),
      },
      {
        path: 'documents',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateDocuments),
        ),
      },
      {
        path: 'settings',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateSettingsPage),
        ),
      },
      {
        path: 'apply',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateApplyPage),
        ),
      },
      {
        path: 'apply/:jobId',
        element: React.createElement(
          Suspense,
          { fallback: React.createElement(LoadingFallback) },
          React.createElement(CandidateApplyPage),
        ),
      },
    ],
  },
  {
    path: '/auth/google/callback',
    element: React.createElement(GoogleAuthCallbackRoute),
  },
  // ── Públicas: portada y documentos legales ──────────────────────
  // Los alias en inglés existen para poder declararlos en la consola de Google
  // sin que el revisor tenga que leer una URL en español.
  { path: '/inicio', element: rutaPublica(PortadaPublica) },
  { path: '/aviso-privacidad', element: rutaPublica(AvisoPrivacidadPage) },
  { path: '/privacy', element: rutaPublica(AvisoPrivacidadPage) },
  { path: '/terminos', element: rutaPublica(TerminosPage) },
  { path: '/terms', element: rutaPublica(TerminosPage) },
  {
    path: '/calendar/:jobId',
    element: React.createElement(ProtectedRoute, {
      requiresAuth: true,
      requiredRole: 'candidato',
      children: React.createElement(Step4_CalendarRoute),
    }),
  },
  {
    path: '/calendar-success/:jobId?',
    element: React.createElement(ProtectedRoute, {
      requiresAuth: true,
      requiredRole: 'candidato',
      children: React.createElement(Step5_CalendarSuccessRoute),
    }),
  },
  {
    path: '/schedule/:jobId',
    element: React.createElement(ProtectedRoute, {
      requiresAuth: true,
      requiredRole: 'candidato',
      children: React.createElement(Step6_ScheduleInterviewRoute),
    }),
  },
  // Un solo RecruiterLayout para /recruiter y /admin evita desmontar la barra lateral al cruzar paneles (CAPDT-187).
  {
    element: React.createElement(ProtectedRoute, {
      requiresAuth: true,
      children: React.createElement(RecruiterLayout),
    }),
    children: [
      {
        path: 'recruiter',
        element: React.createElement(ProtectedOutlet, { requiredRole: 'reclutador' }),
        children: [
          {
            index: true,
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(VacancyDashboardRoute),
            ),
          },
          {
            path: 'new',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(RecruiterHomeRoute),
            ),
          },
          {
            path: 'vacancies/:id/edit',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(VacancyEditorRoute),
            ),
          },
          {
            path: 'workspace/:vacanteId?',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(RecruiterWorkspaceRoute),
            ),
          },
          {
            path: 'metrics/:vacanteId',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(VacancyMetricsRoute),
            ),
          },
          {
            path: 'vacancies/:id/questions',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(VacancyQuestionsReviewRoute),
            ),
          },
          {
            path: 'vacancies/:id/candidate-ficha-preview',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(VacancyPublishedFichaPreviewRoute),
            ),
          },
          {
            path: 'checkout',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(CheckoutRoute),
            ),
          },
          {
            path: 'settings',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(RecruiterSettingsRoute),
            ),
          },
        ],
      },
      {
        path: 'admin',
        element: React.createElement(ProtectedOutlet, { requiredRole: 'admin' }),
        children: [
          {
            index: true,
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(AdminDashboard),
            ),
          },
          {
            path: 'users',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(AdminUsers),
            ),
          },
          {
            path: 'config',
            element: React.createElement(Navigate, {
              to: '/recruiter/settings?tab=administracion',
              replace: true,
            }),
          },
          {
            path: 'audit',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(AdminAudit),
            ),
          },
          {
            path: 'ai-monitoring',
            element: React.createElement(
              Suspense,
              { fallback: React.createElement(LoadingFallback) },
              React.createElement(AdminAIMonitoring),
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/pendiente',
    element: React.createElement(
      Suspense,
      { fallback: React.createElement(LoadingFallback) },
      React.createElement(PendingApprovalRoute),
    ),
  },
  {
    path: '/vacante/:id',
    element: React.createElement(VacantePublicViewRoute),
  },
  {
    path: '/vacancy/:id',
    element: React.createElement(VacantePublicViewRoute),
  },
])
